import  { useState, useEffect, useRef } from "react";
import Icon from "../../../components/AppIcon";

const ProblemSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("before");
  const sectionRef = useRef(null);

  const problemScenarios = [
    {
      title: "Month-End Reconciliation Nightmare",
      description:
        "Finance teams spend 40+ hours manually matching fee payments across multiple spreadsheets",
      icon: "Calculator",
      impact: "40+ hours wasted monthly",
    },
    {
      title: "Scattered Student Data",
      description:
        "Student information spread across 15+ different files with no single source of truth",
      icon: "FileSpreadsheet",
      impact: "15+ disconnected systems",
    },
    {
      title: "Manual Attendance Chaos",
      description:
        "Teachers waste 20 minutes per class on paper-based attendance with frequent errors",
      icon: "ClipboardList",
      impact: "20 min per class lost",
    },
    {
      title: "Delayed Report Generation",
      description:
        "Administrative reports take 3-5 days to compile, making real-time decisions impossible",
      icon: "Clock",
      impact: "3-5 days for basic reports",
    },
  ];

  const solutionBenefits = [
    {
      title: "Instant Fee Reconciliation",
      description:
        "Streamlined fee tracking with instant record updates reduce reconciliation hassles",
      icon: "Zap",
      impact: "Reduces reconciliation hassles",
    },
    {
      title: "Unified Student Profiles",
      description:
        "Single dashboard with complete student journey from admission to graduation",
      icon: "Users",
      impact: "One unified system",
    },
    {
      title: "One-Tap Attendance",
      description:
        "Mobile-first attendance marking for quick and easy tracking",
      icon: "Smartphone",
      impact: "30 seconds per class",
    },
    {
      title: "Real-Time Analytics",
      description:
        "Live dashboards with instant insights for immediate decision-making",
      icon: "BarChart3",
      impact: "Real-time insights",
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about-us"
      ref={sectionRef}
      className="scroll-mt-24 py-10 sm:py-16 lg:py-20 bg-gradient-to-b from-surface to-background"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div
          className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="inline-flex items-center space-x-2 bg-error/10 text-error px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-inter font-600 mb-4 sm:mb-6">
            <Icon name="AlertTriangle" size={16} color="currentColor" />
            <span>The Hidden Cost of Fragmented Systems</span>
          </div>

          <h2 className="font-jakarta font-normal text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-foreground mb-4 sm:mb-6">
            Stop Losing{" "}
            <span className="bg-gradient-to-r from-error to-warning bg-clip-text text-transparent">
              40+ Hours Monthly
            </span>{" "}
            to Administrative Chaos
          </h2>

          <p className="font-inter font-500 text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Progressive institutions are trapped in a cycle of manual processes,
            scattered data, and delayed insights. See how KnowTran transforms
            chaos into streamlined excellence.
          </p>
        </div>

        {/* Before/After Toggle */}
        <div
          className={`flex justify-center mb-10 sm:mb-12 transform transition-all duration-1000 delay-300 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 border border-white/20 aurora-glass">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setActiveTab("before")}
                className={`w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 rounded-xl font-jakarta font-600 text-sm sm:text-base md:text-lg transition-all duration-300 ${
                  activeTab === "before"
                    ? "bg-error text-white shadow-lg"
                    : "bg-white text-muted-foreground hover:text-foreground border border-white/30"
                }`}
              >
                Current Problems
              </button>
              <button
                onClick={() => setActiveTab("after")}
                className={`w-full sm:w-auto px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 rounded-xl font-jakarta font-600 text-sm sm:text-base md:text-lg transition-all duration-300 ${
                  activeTab === "after"
                    ? "bg-success text-white shadow-lg"
                    : "bg-white text-muted-foreground hover:text-foreground border border-white/30"
                }`}
              >
                KnowTran Solution
              </button>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-start">
          {/* Left Side - Visual Representation */}
          <div
            className={`transform transition-all duration-1000 delay-500 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "-translate-x-8 opacity-0"
            }`}
          >
            <div className="relative">
              {activeTab === "before" ? (
                <div className="space-y-6">
                  {/* Chaotic Spreadsheet Visualization */}
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-error/20 shadow-lg">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="font-jakarta font-700 text-base sm:text-lg text-error">
                        Spreadsheet Chaos
                      </h3>
                      <Icon
                        name="AlertCircle"
                        size={20}
                        color="var(--color-error)"
                      />
                    </div>

                    <div className="space-y-3">
                      {[
                        {
                          file: "Student_Data_Final_v3.xlsx",
                          status: "error",
                          size: "2.4 MB",
                        },
                        {
                          file: "Fee_Collection_Oct_2024.xlsx",
                          status: "warning",
                          size: "1.8 MB",
                        },
                        {
                          file: "Attendance_Records_BACKUP.xlsx",
                          status: "error",
                          size: "3.2 MB",
                        },
                        {
                          file: "Exam_Results_TEMP.xlsx",
                          status: "warning",
                          size: "1.1 MB",
                        },
                      ]?.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center space-x-3">
                            <Icon
                              name="FileSpreadsheet"
                              size={16}
                              color="var(--color-muted-foreground)"
                            />
                            <div>
                              <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                                {file?.file}
                              </div>
                              <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground">
                                {file?.size}
                              </div>
                            </div>
                          </div>
                          <Icon
                            name={
                              file?.status === "error" ? "X" : "AlertTriangle"
                            }
                            size={16}
                            color={
                              file?.status === "error"
                                ? "var(--color-error)"
                                : "var(--color-warning)"
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Manual Process Visualization */}
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-warning/20 shadow-lg">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="font-jakarta font-700 text-base sm:text-lg text-warning">
                        Manual Workflows
                      </h3>
                      <Icon
                        name="Clock"
                        size={20}
                        color="var(--color-warning)"
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-error/10 rounded-full flex items-center justify-center">
                          <span className="font-jakarta font-700 text-xs sm:text-sm text-error">
                            1
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                            Collect paper attendance
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground">
                            20 min per class
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-warning/10 rounded-full flex items-center justify-center">
                          <span className="font-jakarta font-700 text-xs sm:text-sm text-warning">
                            2
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                            Manual data entry
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground">
                            2 hours daily
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-error/10 rounded-full flex items-center justify-center">
                          <span className="font-jakarta font-700 text-xs sm:text-sm text-error">
                            3
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                            Cross-reference multiple files
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground">
                            40+ hours monthly
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Unified Dashboard Visualization */}
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-success/20 shadow-lg aurora-glow">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="font-jakarta font-700 text-base sm:text-lg text-success">
                        Unified Platform
                      </h3>
                      <Icon
                        name="CheckCircle"
                        size={20}
                        color="var(--color-success)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      {[
                        {
                          module: "Student Management",
                          status: "active",
                          users: "247",
                        },
                        {
                          module: "Fee Collection",
                          status: "active",
                          users: "89",
                        },
                        {
                          module: "Attendance Tracking",
                          status: "active",
                          users: "156",
                        },
                        {
                          module: "Analytics Dashboard",
                          status: "active",
                          users: "34",
                        },
                      ]?.map((module, index) => (
                        <div
                          key={index}
                          className="p-3 bg-success/5 rounded-lg border border-success/20"
                        >
                          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                            <div className="font-inter font-600 text-xs sm:text-sm text-foreground">
                              {module.module}
                            </div>
                            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-muted-foreground">
                            {module.users} active users
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Automated Process Visualization */}
                  <div className="bg-white rounded-2xl p-5 sm:p-6 border-2 border-primary/20 shadow-lg aurora-glow">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="font-jakarta font-700 text-base sm:text-lg text-primary">
                        Automated Workflows
                      </h3>
                      <Icon name="Zap" size={20} color="var(--color-primary)" />
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
                          <Icon
                            name="Smartphone"
                            size={16}
                            color="var(--color-success)"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                            One-tap mobile attendance
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-success">
                            30 seconds per class
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon
                            name="Database"
                            size={16}
                            color="var(--color-primary)"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                            Real-time data sync
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-primary">
                            Instant updates
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center">
                          <Icon
                            name="BarChart3"
                            size={16}
                            color="var(--color-accent)"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="font-inter font-500 text-xs sm:text-sm text-foreground">
                            Automated reporting
                          </div>
                          <div className="font-inter font-400 text-[10px] sm:text-xs text-accent">
                            Real-time insights
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Problem/Solution List */}
          <div
            className={`transform transition-all duration-1000 delay-700 ${
              isVisible
                ? "translate-x-0 opacity-100"
                : "translate-x-8 opacity-0"
            }`}
          >
            <div className="space-y-6">
              {(activeTab === "before"
                ? problemScenarios
                : solutionBenefits
              )?.map((item, index) => (
                <div
                  key={index}
                  className={`p-4 sm:p-5 lg:p-6 rounded-2xl border-2 transition-all duration-500 hover:shadow-lg ${
                    activeTab === "before"
                      ? "bg-white border-error/20 hover:border-error/40"
                      : "bg-white border-success/20 hover:border-success/40 aurora-glow"
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex items-start space-x-3 sm:space-x-4">
                    <div
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                        activeTab === "before" ? "bg-error/10" : "bg-success/10"
                      }`}
                    >
                      <Icon
                        name={item?.icon}
                        size={22}
                        color={
                          activeTab === "before"
                            ? "var(--color-error)"
                            : "var(--color-success)"
                        }
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-jakarta font-700 text-base sm:text-lg text-foreground mb-1.5 sm:mb-2">
                        {item?.title}
                      </h3>
                      <p className="font-inter font-500 text-sm sm:text-base text-muted-foreground mb-2.5 sm:mb-3 leading-relaxed">
                        {item?.description}
                      </p>
                      <div
                        className={`inline-flex items-center space-x-2 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-inter font-600 ${
                          activeTab === "before"
                            ? "bg-error/10 text-error"
                            : "bg-success/10 text-success"
                        }`}
                      >
                        <Icon
                          name={
                            activeTab === "before"
                              ? "TrendingDown"
                              : "TrendingUp"
                          }
                          size={14}
                          color="currentColor"
                        />
                        <span>{item?.impact}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center mt-14 sm:mt-16 transform transition-all duration-1000 delay-1000 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-6 sm:p-8 border border-primary/20">
            <h3 className="font-jakarta font-700 text-xl sm:text-2xl text-foreground mb-3 sm:mb-4">
              Ready to Transform Your Institution?
            </h3>
            <p className="font-inter font-500 text-sm sm:text-base text-muted-foreground mb-5 sm:mb-6 max-w-2xl mx-auto leading-relaxed">
              Join institutions that have eliminated administrative chaos and
              transformed their operations with KnowTran
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() =>
                  document
                    .getElementById("demo-booking")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl font-jakarta font-600 text-base sm:text-lg hover:bg-primary/90 transition-all duration-300 aurora-glow"
              >
                <Icon name="Calendar" size={20} color="white" />
                <span>Schedule Free Demo</span>
              </button>
              {/* <button
                onClick={() =>
                  document
                    .getElementById("product-tour")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center space-x-2 bg-white text-primary px-8 py-4 rounded-xl font-jakarta font-600 text-lg border-2 border-primary/20 hover:bg-primary/5 transition-all duration-300"
              >
                <Icon name="Play" size={20} color="currentColor" />
                <span>Watch Product Tour</span>
              </button> */}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
